import React from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { oldestYear, TileKind, Buckets } from '../derive-ui';
import { ui } from '../ui';

const tile = 'box-border rounded-[10px] border px-5 py-[18px]';

interface Props {
  kind: TileKind;
  b: Buckets;
  readOnly: boolean;
  /** The artist already sent an identity answer; we owe them a reply. */
  answered: boolean;
  /** ASCAP returned a writer for the legal name: the overlay will ask about that match. */
  onConfirmId: () => void;
}

export function OfferTile({ kind, b, readOnly, answered, onConfirmId }: Props) {
  const { t } = useTranslation('publishing');
  const { missing, ready, hiddenCount } = b;

  if (kind === 'alert') {
    return (
      <div className={`${tile} mt-4 mb-2 border-red-200 bg-red-50`}>
        <h2 className={`m-0 mb-2 ${ui.subhead}`}>{t('offer_blocked_title')}</h2>
        <p className={`m-0 mb-3 ${ui.body}`}>{t('offer_blocked_body')}</p>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={onConfirmId} disabled={readOnly} className={ui.btnPrimary}>
            {t('offer_blocked_cta')}
          </Button>
          {answered && <span className={ui.muted}>{t('offer_blocked_answered')}</span>}
        </div>
      </div>
    );
  }

  if (kind === 'ok') {
    return (
      <div className={`${tile} mt-4 mb-2 border-green-200 bg-green-50`}>
        <div className="flex items-start gap-3">
          <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-green-700" />
          <div>
            <h2 className={`m-0 mb-2 ${ui.subhead}`}>{t('offer_ok_title')}</h2>
            <p className={`m-0 ${ui.body}`}>{t('offer_ok_body', { count: ready.length })}</p>
            {!!hiddenCount && (
              <p className={`m-0 mt-3 ${ui.muted}`}>
                {t('offer_ok_hidden', { count: hiddenCount })}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (kind === 'warn') {
    return (
      <div className="mt-4 mb-2 box-border rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className={`m-0 mb-2 ${ui.subhead}`}>{t('offer_warn_title')}</h2>
        <p className={`m-0 ${ui.body}`}>
          {hiddenCount ? t('offer_warn_hidden') : t('offer_warn_none')}
        </p>
      </div>
    );
  }

  const oldest = oldestYear(missing);
  const since =
    oldest && oldest < new Date().getFullYear() ? ' ' + t('offer_cta_since', { year: oldest }) : '';
  return (
    <div className={`${tile} mt-4 mb-2 border-amber-200 bg-amber-50`}>
      <div className="flex items-start gap-3">
        <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-700" />
        <div>
          <h2 className={`m-0 mb-2 ${ui.subhead}`}>
            {t('offer_cta_listed', { count: missing.length })}
          </h2>
          <p className={`m-0 ${ui.body}`}>
            {t('offer_cta_wrote', { count: missing.length })}
            {since}
          </p>
        </div>
      </div>
    </div>
  );
}
